import { useSearchParams } from 'react-router-dom';
import { useCallback, useMemo } from 'react';

export function useQueryParamArray(paramName: string): [string[], (newValue: string[]) => void] {
  const [searchParams, setSearchParams] = useSearchParams();

  // Memoize the array creation to prevent unnecessary re-renders
  const value = useMemo(() => {
    const paramValue = searchParams.get(paramName);
    // If the param exists, split it by comma. Otherwise, return an empty array.
    // filter(Boolean) removes any empty strings that might result from trailing commas.
    return paramValue ? paramValue.split(',').filter(Boolean) : [];
  }, [searchParams, paramName]);

  const setValue = useCallback(
    (newValue: string[]) => {
      // Use the updater function to get the latest search params
      setSearchParams((prevSearchParams) => {
        // 1. Create a NEW URLSearchParams object from the previous one
        const newSearchParams = new URLSearchParams(prevSearchParams);

        // 2. Modify the NEW object
        if (!newValue || newValue.length === 0) {
          newSearchParams.delete(paramName);
        } else {
          newSearchParams.set(paramName, newValue.join(','));
        }

        // 3. Return the NEW object to trigger an update
        return newSearchParams;
      });
    },
    [paramName, searchParams, setSearchParams] // Note: `searchParams` is removed from deps
  );

  return [value, setValue];
}